import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react';
import type { JSX } from 'react';

import { AlertGroupSummary, BillingReportRow, ErrorsByField } from '../types';

import AlertSummaryList from './AlertSummaryList';

/**
 * Flattens the client-side aggregate into the normalized model AlertSummaryList
 * renders, sorted the way the summary endpoint sorts its own output -- groups by
 * total, then messages by count -- so both Alert Summary panels read the same.
 * No alert_id: these counts come from the rows' errors/warnings text, which
 * carries no BillingAlert reference.
 */
const toAlertGroups = (messagesByField: ErrorsByField): AlertGroupSummary[] =>
  Object.entries(messagesByField)
    .map(([field, { count, errors }]) => ({
      group: field,
      count,
      messages: Object.entries(errors)
        .map(([description, messageCount]) => ({ description, count: messageCount }))
        .sort((a, b) => b.count - a.count)
    }))
    .sort((a, b) => b.count - a.count);

// Helper function to group messages by field
const groupMessagesByField = (
  rows: BillingReportRow[],
  messageType: 'errors' | 'warnings'
): ErrorsByField => {
  const result: ErrorsByField = {};

  rows.forEach((row) => {
    const messages = row[messageType] || {};
    Object.entries(messages).forEach(([field, message]: [string, string[]]) => {
      if (!result[field]) {
        result[field] = { count: 0, errors: {} };
      }
      message.forEach((e) => {
        // Skip Submitted rows
        if (
          (row.cms_1500_healthie_id && row.cms_1500_healthie_id.length > 0) ||
          (row.indicators && row.indicators.includes('non_billable'))
        ) {
          return;
        }
        if (!result[field].errors[e]) {
          result[field].errors[e] = 0;
        }
        result[field].errors[e] += 1;
        result[field].count += 1;
      });
    });
  });

  return result;
};

export const BillingReportErrorSummary = ({
  rows,
  searchForError,
  searchForWarning
}: {
  rows: BillingReportRow[];
  searchForError: (error: string, onlyUnbilled: boolean) => void;
  searchForWarning: (error: string, onlyUnbilled: boolean) => void;
}): JSX.Element => {
  // Find all rows with errors
  const rowsWithErrors = useMemo(
    () => rows.filter((row) => row.errors && Object.keys(row.errors || {}).length),
    [rows]
  );

  const rowsWithWarnings = useMemo(
    () => rows.filter((row) => row.warnings && Object.keys(row.warnings || {}).length),
    [rows]
  );

  // Find all errors, grouped by field, and count unique field errors
  const errorsByField = useMemo(
    () => groupMessagesByField(rowsWithErrors, 'errors'),
    [rowsWithErrors]
  );

  const warningsByField = useMemo(
    () => groupMessagesByField(rowsWithWarnings, 'warnings'),
    [rowsWithWarnings]
  );

  return (
    <div className="p-4 bg-white border border-gray-300 shadow" data-testid="errors_summary">
      <h2 className="text-lg font-bold">Alert Summary</h2>

      <p className="text-xs text-gray-500">
        <FontAwesomeIcon icon={faInfoCircle} className="mr-1" />
        Items are counted by the number of rows that contain the error. Rows with multiple errors
        could be counted multiple times.
      </p>

      <h3 className="font-bold pt-2">Errors</h3>
      <AlertSummaryList
        groups={toAlertGroups(errorsByField)}
        type="error"
        search={(message) => searchForError(message.description, false)}
        searchUnbilled={(message) => searchForError(message.description, true)}
      />

      <h3 className="font-bold pt-2">Warnings</h3>

      <AlertSummaryList
        groups={toAlertGroups(warningsByField)}
        type="warning"
        search={(message) => searchForWarning(message.description, false)}
        searchUnbilled={(message) => searchForWarning(message.description, true)}
      />
    </div>
  );
};
